import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ActionRole, ButtonType } from '@newroom-connect/library/enums';
import { IActionButton, IActionButtonEvent, IListItemClickedEvent, IListOptionsSort, ListItemType, IListConfiguration, IListItem } from '@newroom-connect/library/interfaces';
import { ExtractPropertyPipe, ToStringPipe, ExtractPropertiesPipe, JoinPipe, FirstLetterPipe } from '@newroom-connect/library/pipes';
import { InfiniteScrollDirective } from '@newroom-connect/library/directives';

import { BaseListComponent } from '../abstract/base-list.component';

import { RowCardItemComponent } from './row-card-item/row-card-item.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RowCardItemComponent,
    InfiniteScrollDirective,
    ExtractPropertyPipe,
    ToStringPipe,
    ExtractPropertiesPipe,
    JoinPipe,
    FirstLetterPipe
  ],
  selector: 'nrc-row-card-list',
  templateUrl: './row-card-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RowCardListComponent extends BaseListComponent {
  @Input({ required: true }) public config!: IListConfiguration;

  @Output() public sortEvent = new EventEmitter<IListOptionsSort>();
  @Output() public rowCardItemClickedEvent = new EventEmitter<IListItemClickedEvent>();
  @Output() public itemActionButtonEvent = new EventEmitter<IActionButtonEvent>();
  @Output() public configButtonClickEvent = new EventEmitter<{ index: number, key: string }>();

  public listItemType = ListItemType;
  public actionRole = ActionRole;
  public buttonType = ButtonType;

  /**
   * Handle the event when a row card item is clicked.
   *
   * @param item The row card item that was clicked.
   * @param itemIndex The index of the clicked item inside the list.
   */
  public handleRowCardItemClickedEvent(item: IListItem, itemIndex: number): void {
    const items = this.items();

    if (!items) {
      return;
    }

    this.rowCardItemClickedEvent.emit({ index: itemIndex, item });

    // Toggle the selection state of the items if multi-selection is enabled
    if (this.config.selectMultiple) {
      this.toggleItemIsSelectedAtIndex(itemIndex);

      return;
    }

    // Single selection mode
    this.handleSingleSelection(items, itemIndex);
  }

  /**
   * Handle item action button events.
   *
   * @param event
   * @param event.index
   * @param event.actionButton
   */
  public handleItemAction(event: { index: number, actionButton: IActionButton }): void {
    const item = this.items()?.[event.index];

    if (!item) {
      return;
    }

    this.itemActionButtonEvent.emit({
      value: item,
      actionButton: event.actionButton,
      index: event.index
    });
  }

  /**
   * Toggle the selection state of an item in the `items` array at the specified index.
   *
   * @param items The items array to toggle.
   * @param itemIndex The index of the item in the `items` array to toggle.
   */
  private handleSingleSelection(items: IListItem[], itemIndex: number): void {
    for (let i = 0; i < items.length; i++) {
      this.items.update(items => {
        if (!items) {
          return null;
        }

        items[i].isSelected = itemIndex === i ? !items[i].isSelected : false;

        return [...items];
      });
    }
  }
}
